// Per-admin "Access" capabilities (master-admin settings → Store Admin column).
// Generic on purpose: the one "Access" multi-select is meant to carry more
// feature toggles over time. For now the only capability is CRM visibility —
// holding it lets the admin browse the full CRM directories; without it the
// frontend keeps those lists empty until a search is typed (privacy).
//
// Mirrors the backend User::adminAccessCapabilities(): master admins get every
// capability by default (until narrowed explicitly), everyone else is grant-based.

export const ACCESS = {
  CRM_VISIBILITY: "crm_visibility",
  REMOVE_PAYMENT: "remove_payment",
  CREATE_PRODUCT: "create_product",
  EDIT_DEAL_DATE: "edit_deal_date",
  MOVE_TRANSACTION: "move_transaction",
};

// Options shown in the master-admin Store Admin "Access" multi-select, in order.
export const ACCESS_OPTIONS = [
  { key: ACCESS.CRM_VISIBILITY, label: "CRM Visibility" },
  { key: ACCESS.REMOVE_PAYMENT, label: "Remove Payment" },
  { key: ACCESS.CREATE_PRODUCT, label: "Create Product" },
  { key: ACCESS.EDIT_DEAL_DATE, label: "Edit Deal Date" },
  { key: ACCESS.MOVE_TRANSACTION, label: "Move Transaction" },
];

/**
 * True if the admin holds an "Access" capability.
 *
 * Authoritative source is `admin_access_capabilities` — the backend-resolved list
 * (master admins with no explicit grant get all keys), so the frontend gate and
 * the grant can never disagree. The `is_master_admin` branch is only a fallback
 * for a stale user object cached before this field existed, so the gate still
 * fails closed for regular admins.
 */
export const canAdminAccess = (user, key) => {
  if (!user) return false;
  if (Array.isArray(user.admin_access_capabilities)) {
    return user.admin_access_capabilities.includes(key);
  }
  return !!user.is_master_admin;
};

/** Convenience: may this admin browse the full CRM directories without searching? */
export const hasCrmVisibility = (user) => canAdminAccess(user, ACCESS.CRM_VISIBILITY);

/** Convenience: may this admin add a new row to the (shared) product catalogue? */
export const canCreateProduct = (user) => canAdminAccess(user, ACCESS.CREATE_PRODUCT);

/**
 * May this user create catalogue entries at all? Mirrors the backend
 * ProductPolicy::create() since the store-catalog feature: masters and
 * delegated CREATE_PRODUCT holders write the SHARED catalogue; any store
 * admin/owner may now submit entries for THEIR OWN store (held for RC
 * approval unless auto-approve is on). Gates /add-new-product and the
 * Owner Admin → My Products adders.
 */
export const canSubmitCatalogEntries = (user, accessibleStores, activeStoreId) =>
  canCreateProduct(user) ||
  !!user?.is_admin ||
  activeStoreRole(user, accessibleStores, activeStoreId) === "owner";

/**
 * May this admin move a deal's creation date between months (Kanban, P&L,
 * reconciliation AND the Xero document date)? Backend enforces the same key —
 * User::ACCESS_EDIT_DEAL_DATE on PUT /requests/:id/deal-date.
 */
export const canEditDealDate = (user) => canAdminAccess(user, ACCESS.EDIT_DEAL_DATE);

/**
 * May this admin re-file a whole deal under a different store? Backend enforces
 * the same key — User::ACCESS_MOVE_TRANSACTION on the /move-transaction routes,
 * which ALSO require store_user membership of both the source and target store.
 *
 * Deliberately NOT gated by useStoreWriteGuard: unlike New Listing or Save
 * Payment, this action has no ambiguous destination even from "Master Store" —
 * the source comes from the deal itself and the target is chosen explicitly in
 * the dialog. Master Store is also the only place a deal from every store is
 * visible, which makes it the natural place to re-file one.
 */
export const canMoveTransaction = (user) => canAdminAccess(user, ACCESS.MOVE_TRANSACTION);

/**
 * Is this deal finished enough for its date to be moved? Mirrors
 * DealDateService::isEligible — a request marked done, or an order Xero has
 * been told is settled. An in-flight deal is excluded because the normal save
 * flow still re-derives its date from the payment, so any manual value there
 * would be silently overwritten.
 *
 * `item.order` is eager-loaded as {id, status} on the requests list for exactly
 * this check (RequestService: `'order:id,status'`).
 */
export const isDealDateEditable = (item) =>
  item?.status === "done" || item?.order?.status === "paid";

/**
 * This user's store_user role (owner/store_admin/sales_admin) in the
 * CURRENTLY ACTIVE store (Phase 5 store-switcher) — the frontend mirror of
 * the backend's `TenantContext::currentRole()`. `accessibleStores` (fetched
 * from GET /api/stores/accessible into UserContext) already carries `.role`
 * per store; this just picks out the one matching `activeStoreId` (falling
 * back to whichever store is flagged `is_default`, same as StoreSwitcher's
 * own fallback, for a user who hasn't switched yet).
 *
 * Returns null for a master admin (cross-store, no single store_user role
 * applies) or when accessibleStores hasn't loaded/has no match.
 */
export const activeStoreRole = (user, accessibleStores, activeStoreId) => {
  if (!user || user.is_master_admin || !Array.isArray(accessibleStores)) {
    return null;
  }

  const store =
    accessibleStores.find((s) => s.id === activeStoreId) ??
    accessibleStores.find((s) => s.is_default);

  return store?.role ?? null;
};

/**
 * Is this user the OWNER (Primary admin) of the currently active store?
 * Mirrors the backend's `User::isPrimaryAdmin()` — which, for a user
 * checking their own access, already resolves via the active store, not
 * store_id_m. Frontend gates that used the raw `is_store_owner` flag
 * (fixed to their home store) disagreed with the backend the moment a
 * multi-store owner switched into a store they don't own at home.
 */
export const isActiveStoreOwner = (user, accessibleStores, activeStoreId) =>
  !!user?.is_master_admin || activeStoreRole(user, accessibleStores, activeStoreId) === "owner";
